import React, { useState, useEffect } from 'react';
import {
  VStack, Input, Select, Textarea, Text, Box, Button, InputGroup,
  InputRightElement, useToast, SimpleGrid, Divider, Heading
} from '@chakra-ui/react';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import SelectorPerfilUsuario from 'components/PerfilUsuario/SelectorPerfilUsuario';
import { GOOGLE_MAPS_API_KEY } from 'config/googleMaps';

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  data?: any;
  onSubmit?: (data: any) => void;
}

const tarifas = {
  Capilla: 5000,
  Monumento: 3000,
  Gaveta: 1500,
};

const SolicitudPanteon: React.FC<Props> = ({ modo, data, onSubmit }) => {
  const toast = useToast();

  const [tipo, setTipo] = useState(data?.tipo || '');
  const [solicitante, setSolicitante] = useState(data?.solicitante || '');
  const [difunto, setDifunto] = useState(data?.difunto || '');
  const [ancho, setAncho] = useState(data?.ancho || '');
  const [largo, setLargo] = useState(data?.largo || '');
  const [direccion, setDireccion] = useState(data?.direccion || '');
  const [lat, setLat] = useState(Number(data?.lat) || 19.4326);
  const [lng, setLng] = useState(Number(data?.lng) || -99.1332);
  const [observaciones, setObservaciones] = useState(data?.observaciones || '');
  const [costo, setCosto] = useState(data?.costo || 0);
  const [perfilSeleccionado, setPerfilSeleccionado] = useState<any>(null);

  const handlePerfil = (perfil: any) => {
    setPerfilSeleccionado(perfil);
  };

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: ['places'],
  });

  useEffect(() => {
    const area = parseFloat(ancho) * parseFloat(largo);
    const tarifa = tarifas[tipo as keyof typeof tarifas] || 0;
    setCosto(area * tarifa);
  }, [tipo, ancho, largo]);

  const buscarDireccion = () => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: direccion }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const loc = results[0].geometry.location;
        setLat(loc.lat());
        setLng(loc.lng());
        setDireccion(results[0].formatted_address);
      } else {
        toast({
          title: 'Dirección no encontrada',
          description: 'Revisa la dirección ingresada.',
          status: 'error',
        });
      }
    });
  };

  const handleGuardar = async () => {
    const payload = {
      tipo,
      solicitante,
      difunto,
      ancho,
      largo,
      direccion,
      lat,
      lng,
      observaciones,
      costo,
      fechaRegistro: new Date(),
      usuarioId: perfilSeleccionado?.id || null,
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/obras/solicitud-panteon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Error al guardar');

      toast({
        title: 'Guardado',
        description: 'La solicitud fue registrada.',
        status: 'success',
      });

      onSubmit?.(payload);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Hubo un error al registrar.',
        status: 'error',
      });
    }
  };

  if (!isLoaded) return <Text>Cargando mapa...</Text>;

  return (
    <VStack spacing={6} align="stretch">
      {(modo === 'edit' || modo === 'new') ? (
        <>
          <Heading size="md">Datos del Solicitante</Heading>
          <SelectorPerfilUsuario onPerfilSeleccionado={handlePerfil} />

          <Divider />

          <Heading size="md">Información de la Construcción</Heading>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            <Select placeholder="Tipo de construcción" value={tipo} onChange={(e) => setTipo(e.target.value)}>
              {Object.keys(tarifas).map((key) => (
                <option key={key} value={key}>{key}</option>
              ))}
            </Select>
            <Input placeholder="Solicitante" value={solicitante} onChange={(e) => setSolicitante(e.target.value)} />
            <Input placeholder="Nombre del difunto" value={difunto} onChange={(e) => setDifunto(e.target.value)} />
            <Input type="number" placeholder="Ancho (m)" value={ancho} onChange={(e) => setAncho(parseFloat(e.target.value))} />
            <Input type="number" placeholder="Largo (m)" value={largo} onChange={(e) => setLargo(parseFloat(e.target.value))} />
          </SimpleGrid>

          <Text fontWeight="semibold" color="green.600">
            Costo estimado: ${costo.toFixed(2)}
          </Text>

          <Divider />

          <Heading size="md">Ubicación del Proyecto</Heading>
          <InputGroup>
            <Input placeholder="Dirección" value={direccion} onChange={(e) => setDireccion(e.target.value)} />
            <InputRightElement width="4.5rem">
              <Button h="1.75rem" size="sm" onClick={buscarDireccion}>Buscar</Button>
            </InputRightElement>
          </InputGroup>

          <Box h="250px" w="100%" borderRadius="md" overflow="hidden" border="1px solid #ccc">
            <GoogleMap zoom={15} center={{ lat, lng }} mapContainerStyle={{ width: '100%', height: '100%' }}>
              <Marker
                position={{ lat, lng }}
                draggable
                onDragEnd={(e) => {
                  setLat(e.latLng?.lat() || lat);
                  setLng(e.latLng?.lng() || lng);
                }}
              />
            </GoogleMap>
          </Box>

          <Textarea placeholder="Observaciones" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />

          <Button colorScheme="green" onClick={handleGuardar}>
            {modo === 'new' ? 'Registrar Solicitud' : 'Actualizar Solicitud'}
          </Button>
        </>
      ) : (
        <>
          <Heading size="lg" mb={2}>Resumen de Solicitud</Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <Box borderWidth="1px" borderRadius="md" p={4} bg="gray.50">
              <Text fontWeight="bold" mb={2}>Información General</Text>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
                <Text><strong>Tipo:</strong> {tipo}</Text>
                <Text><strong>Solicitante:</strong> {solicitante}</Text>
                <Text><strong>Difunto:</strong> {difunto}</Text>
                <Text><strong>Dimensiones:</strong> {ancho} m x {largo} m</Text>
                <Text><strong>Dirección:</strong> {direccion}</Text>
                <Text><strong>Costo:</strong> ${costo.toFixed(2)}</Text>
                <Text><strong>Fecha:</strong> {data?.fechaRegistro ? new Date(data.fechaRegistro).toLocaleString() : 'No disponible'}</Text>
              </SimpleGrid>
            </Box>

            {observaciones && (
              <Box borderWidth="1px" borderRadius="md" p={4} bg="gray.50">
                <Text fontWeight="bold" mb={2}>Observaciones</Text>
                <Text>{observaciones}</Text>
              </Box>
            )}
          </SimpleGrid>

          <Box h="250px" w="100%" borderRadius="md" overflow="hidden" border="1px solid #ccc">
            <GoogleMap zoom={15} center={{ lat, lng }} mapContainerStyle={{ width: '100%', height: '100%' }}>
              <Marker position={{ lat, lng }} />
            </GoogleMap>
          </Box>
        </>
      )}
    </VStack>
  );
};

export default SolicitudPanteon;
